import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Plus,
  Search,
  SlidersHorizontal,
  XCircle,
} from 'lucide-react';
import { BackButton, Button, Card, Chip, Input, Select } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { ApiError, api } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { formatName } from '@/lib/format';
import { useClientes } from '@/modules/clientes/api';
import { encodeClienteId } from '@/modules/clientes/cliente-id';
import { CafAlertBanner } from '@/modules/clientes/components/CafAlertBanner';
import { ClientesSegmentos } from '@/modules/clientes/components/ClientesSegmentos';
import { ClienteList } from '@/modules/clientes/components/ClienteList';
import { ProdutorRuralList } from '@/modules/clientes/components/ProdutorRuralList';
import { ImovelDetailModal } from '@/modules/clientes/components/ImovelDetailModal';
import { STATUS_CLIENTE, type ImovelResumo, type StatusCliente } from '@/modules/clientes/schemas';
import { STATUS_LABEL } from '@/modules/clientes/data';
import {
  useClientesDoGrupo,
  useGruposEconomicos,
  useImoveisDoGrupo,
} from '@/modules/grupos-economicos/api';
import { GrupoList } from '@/modules/grupos-economicos/components/GrupoList';
import { GrupoEconomicoModal } from '@/modules/grupos-economicos/components/GrupoEconomicoModal';

type Pipe = 'rural' | 'conformidade' | 'grupo-economico';

export const Route = createFileRoute('/_authed/clientes')({
  component: ClientesPage,
  validateSearch: (s: Record<string, unknown>): { pipe?: Pipe; grupo?: number } => {
    const p = s.pipe;
    const validPipe =
      p === 'rural' || p === 'conformidade' || p === 'grupo-economico' ? p : undefined;
    const grupoRaw = s.grupo;
    const grupoNum =
      typeof grupoRaw === 'number'
        ? grupoRaw
        : typeof grupoRaw === 'string' && /^\d+$/.test(grupoRaw)
          ? Number(grupoRaw)
          : undefined;
    const out: { pipe?: Pipe; grupo?: number } = {};
    if (validPipe) out.pipe = validPipe;
    if (validPipe === 'grupo-economico' && grupoNum) out.grupo = grupoNum;
    return out;
  },
});

const confCheckSchema = z.object({
  label: z.string(),
  status: z.string(),
  detalhe: z.string().nullish(),
});
const confDocumentoSchema = z
  .object({
    id: z.union([z.string(), z.number()]).nullish(),
    tipo: z.string().nullish(),
    descricao: z.string().nullish(),
    status: z.string().nullish(),
    validade: z.string().nullish(),
    created_at: z.string().nullish(),
  })
  .passthrough();
const confAreaSchema = z
  .object({
    kind: z.string(),
    id: z.union([z.string(), z.number()]),
    nome: z.string().nullish(),
    matricula: z.string().nullish(),
    area_ha: z.union([z.string(), z.number()]).nullish(),
    arrendador_nome: z.string().nullish(),
    onus_items: z.array(z.record(z.string(), z.unknown())).default([]),
    conformidade: z.object({
      docs_relacionados: z.number(),
      documentos: z.array(confDocumentoSchema).default([]),
      checks: z.array(confCheckSchema),
      score: z.number(),
      pendencias: z.number(),
    }),
  })
  .passthrough();
const confPayloadSchema = z.object({
  resumo: z.object({
    total_clientes: z.number(),
    total_areas: z.number(),
    pendencias: z.number(),
    areas_sem_documentos: z.number(),
  }),
  clientes: z.array(
    z.object({
      cpf: z.string(),
      nome: z.string(),
      municipio: z.string().nullish(),
      uf: z.string().nullish(),
      score: z.number(),
      pendencias: z.number(),
      instituicoes: z.array(z.string()).default([]),
      areas: z.array(confAreaSchema),
    }),
  ),
});
type ConfCliente = z.infer<typeof confPayloadSchema>['clientes'][number];
type ConfArea = ConfCliente['areas'][number];
type ConfCheck = z.infer<typeof confCheckSchema>;

function ClientesPage() {
  const navigate = useNavigate();
  const { pipe, grupo: grupoId } = Route.useSearch();
  const setPipe = (p: Pipe | null) =>
    void navigate({
      to: '/clientes',
      search: p ? { pipe: p } : {},
      replace: true,
    });
  const setGrupo = (id: number | null) =>
    void navigate({
      to: '/clientes',
      search: id ? { pipe: 'grupo-economico', grupo: id } : { pipe: 'grupo-economico' },
      replace: true,
    });
  const { data: clientes = [], isLoading, error } = useClientes();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusCliente | ''>('');
  const [ufFilter, setUfFilter] = useState('');
  const [municipioFilter, setMunicipioFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeCount = (statusFilter ? 1 : 0) + (ufFilter ? 1 : 0) + (municipioFilter ? 1 : 0);
  const filtroAtivo = activeCount > 0 || !!search.trim();

  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!filtersOpen) return undefined;
    function onDown(e: MouseEvent) {
      if (filterWrapRef.current && !filterWrapRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setFiltersOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  function limparFiltros() {
    setSearch('');
    setStatusFilter('');
    setUfFilter('');
    setMunicipioFilter('');
  }
  const ufsDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const c of clientes) if (c.uf) set.add(c.uf.toUpperCase());
    return Array.from(set).sort();
  }, [clientes]);
  // Cidades vêm filtradas pela UF selecionada (se houver) — evita mostrar
  // município de outro estado. Deduplica case-insensitive (banco tem
  // 'CEREJEIRAS' e 'Cerejeiras' como variantes da mesma cidade).
  const municipiosDisponiveis = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const c of clientes) {
      if (!c.municipio) continue;
      if (ufFilter && (c.uf ?? '').toUpperCase() !== ufFilter.toUpperCase()) continue;
      const key = c.municipio.toLowerCase().trim();
      if (!byKey.has(key)) byKey.set(key, formatName(c.municipio));
    }
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [clientes, ufFilter]);

  // Seleção de segmento — layout bento (novo design ARA)
  if (!pipe) {
    return (
      <div className="flex min-h-full flex-col">
        <div className="flex-1 px-8 pb-8 pt-8">
          <ClientesSegmentos
            produtorCount={clientes.length}
            onSelect={(id) => {
              if (id === 'diagnostico-agro') {
                void navigate({ to: '/diagnostico' });
                return;
              }
              if (id === 'conformidade') return setPipe('conformidade');
              if (id === 'grupo-economico') return setPipe('grupo-economico');
              setPipe('rural');
            }}
          />
        </div>
      </div>
    );
  }

  if (pipe === 'grupo-economico') {
    return (
      <GrupoEconomicoPipe
        grupoId={grupoId ?? null}
        onBackToPipes={() => setPipe(null)}
        onSelectGrupo={(id) => setGrupo(id)}
        onBackToGrupos={() => setGrupo(null)}
        onSelectCliente={(cpf) =>
          void navigate({
            to: '/clientes/$id',
            params: { id: encodeClienteId(cpf) },
            search: {
              relatorio: undefined,
              tab: undefined,
              from_pipe: 'grupo-economico',
              from_grupo: grupoId,
            },
          })
        }
      />
    );
  }

  if (pipe === 'conformidade') {
    return (
      <PageShell
        eyebrow="Negócios · Cliente"
        title="Conformidade documental"
        lead="Controle documental por cliente cadastrado, incluindo imóveis próprios e áreas arrendadas."
        back={
          <Button variant="ghost" onClick={() => setPipe(null)}>
            <ArrowLeft size={14} strokeWidth={1.5} />
            Voltar
          </Button>
        }
      >
        <ConformidadeClientesView />
      </PageShell>
    );
  }

  // Pipe "rural" — lista de clientes (design ARA · sem cabeçalho)
  return (
    <div className="flex min-h-full flex-col">
      <div className="flex-1 px-8 pb-8 pt-8">
        <Stack gap="lg">
          <div className="flex items-center justify-between gap-4">
            <BackButton onClick={() => setPipe(null)} label="Voltar para seleção" />
            <span className="text-sm text-text-muted">{clientes.length} clientes</span>
          </div>

          <CafAlertBanner clientes={clientes} />

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 md:max-w-md">
              <Search
                size={14}
                strokeWidth={1.5}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
              />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, CPF ou município"
                className="rounded-full pr-9"
              />
            </div>

            <div ref={filterWrapRef} className="relative">
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[0.78rem]',
                  'border border-border transition-colors',
                  activeCount > 0 || filtersOpen
                    ? 'bg-[rgba(217,119,87,0.1)] text-accent border-transparent'
                    : 'bg-elevated text-text-2 hover:bg-surface2 hover:text-text',
                )}
              >
                <SlidersHorizontal size={13} strokeWidth={1.5} />
                Filtrar por
                {activeCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 ml-1 rounded-full bg-accent text-[var(--on-solid)] text-[0.62rem] font-bold leading-none">
                    {activeCount}
                  </span>
                )}
              </button>

              {filtersOpen && (
                <div
                  className={cn(
                    'absolute top-[calc(100%+6px)] left-0 z-50',
                    'bg-surface border border-border rounded-xl shadow-[var(--shadow-over)]',
                    'p-4 min-w-[280px] flex flex-col gap-3',
                  )}
                >
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="cli-flt-status"
                      className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                    >
                      Status
                    </label>
                    <Select
                      id="cli-flt-status"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as StatusCliente | '')}
                      className="text-[0.82rem]"
                    >
                      <option value="">Todos</option>
                      {STATUS_CLIENTE.map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABEL[s]}
                        </option>
                      ))}
                    </Select>
                  </div>

                  {ufsDisponiveis.length > 1 && (
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor="cli-flt-uf"
                        className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                      >
                        UF
                      </label>
                      <Select
                        id="cli-flt-uf"
                        value={ufFilter}
                        onChange={(e) => {
                          setUfFilter(e.target.value);
                          setMunicipioFilter('');
                        }}
                        className="text-[0.82rem]"
                      >
                        <option value="">Todas</option>
                        {ufsDisponiveis.map((uf) => (
                          <option key={uf} value={uf}>
                            {uf}
                          </option>
                        ))}
                      </Select>
                    </div>
                  )}

                  {municipiosDisponiveis.length > 1 && (
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor="cli-flt-municipio"
                        className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                      >
                        Cidade
                      </label>
                      <Select
                        id="cli-flt-municipio"
                        value={municipioFilter}
                        onChange={(e) => setMunicipioFilter(e.target.value)}
                        className="text-[0.82rem]"
                      >
                        <option value="">Todas</option>
                        {municipiosDisponiveis.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </Select>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={limparFiltros}
                    disabled={!filtroAtivo}
                    className={cn(
                      'self-start text-[0.78rem] text-accent bg-transparent border-0 p-0',
                      'hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline',
                    )}
                  >
                    Limpar filtros
                  </button>
                </div>
              )}
            </div>
          </div>

          {isLoading ? (
            <Card padding="lg">
              <p className="text-sm text-text-muted">Carregando carteira…</p>
            </Card>
          ) : error ? (
            <Card padding="lg">
              <p className="text-sm text-danger">
                {error instanceof ApiError ? error.message : 'Falha ao carregar clientes.'}
              </p>
            </Card>
          ) : clientes.length === 0 ? (
            <Card padding="lg">
              <p className="text-sm text-text-muted">Nenhum cliente cadastrado ainda.</p>
            </Card>
          ) : (
            <ProdutorRuralList
              clientes={clientes}
              search={search}
              statusFilter={statusFilter}
              ufFilter={ufFilter}
              municipioFilter={municipioFilter}
              onSelect={(cpf) =>
                void navigate({
                  to: '/clientes/$id',
                  params: { id: encodeClienteId(cpf) },
                  search: {
                    relatorio: undefined,
                    tab: undefined,
                    from_pipe: 'rural',
                    from_grupo: undefined,
                  },
                })
              }
            />
          )}
        </Stack>
      </div>
    </div>
  );
}

function ConformidadeClientesView() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['clientes', 'conformidade-documental'],
    queryFn: ({ signal }) =>
      api('/api/v2/credito/conformidade-docs', { schema: confPayloadSchema, signal }),
    staleTime: 60_000,
  });
  if (isLoading)
    return (
      <Card padding="lg">
        <p className="text-sm text-text-muted">Carregando conformidade…</p>
      </Card>
    );
  if (error)
    return (
      <Card padding="lg">
        <p className="text-sm text-danger">Falha ao carregar conformidade documental.</p>
      </Card>
    );
  if (!data) return null;
  return (
    <Stack gap="md">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <ConfStat label="Clientes" value={data.resumo.total_clientes} />
        <ConfStat label="Áreas" value={data.resumo.total_areas} />
        <ConfStat label="Pendências" value={data.resumo.pendencias} danger />
        <ConfStat label="Áreas sem docs" value={data.resumo.areas_sem_documentos} danger />
      </div>
      <ConformidadeTabela clientes={data.clientes} />
    </Stack>
  );
}
function ConfStat({
  label,
  value,
  danger = false,
}: {
  label: string;
  value: number;
  danger?: boolean;
}) {
  return (
    <Card padding="md">
      <div className="text-[0.65rem] uppercase tracking-wide text-text-muted font-semibold">
        {label}
      </div>
      <div className={`text-2xl font-bold ${danger ? 'text-danger' : 'text-text'}`}>
        {value.toLocaleString('pt-BR')}
      </div>
    </Card>
  );
}

export function relatorioConformidadeUrl(cpf: string, tipo: 'compacto' | 'expandido') {
  const relatorio = tipo === 'expandido' ? 'conformidade-documental' : 'conformidade-resumido';
  return `/clientes/${encodeClienteId(cpf)}/relatorio/${relatorio}?imprimir=1`;
}

function abrirRelatorioConformidade(cliente: ConfCliente, tipo: 'compacto' | 'expandido') {
  const popup = window.open('', '_blank');
  if (!popup) {
    window.alert('Permita a abertura de janelas neste navegador para emitir o relatório.');
    return;
  }
  popup.opener = null;
  popup.location.href = relatorioConformidadeUrl(cliente.cpf, tipo);
}

async function baixarRelatorioOnus(cliente: ConfCliente) {
  const resp = await fetch('/api/v2/credito/onus-gravames/relatorio-pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ cliente }),
  });
  if (!resp.ok) throw new Error('Falha ao gerar relatório de ônus/gravames');
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safe =
    cliente.nome
      .replace(/[^a-zA-Z0-9À-ú\s_-]/g, '')
      .trim()
      .replace(/\s+/g, '_') || 'cliente';
  a.href = url;
  a.download = `Onus_Gravames_${safe}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

function ConformidadeTabela({ clientes }: { clientes: ConfCliente[] }) {
  const sorted = clientes
    .slice()
    .sort((a, b) => b.pendencias - a.pendencias || a.nome.localeCompare(b.nome));

  return (
    <Card padding="none">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface2 text-text-muted">
            <tr>
              <Th>Cliente</Th>
              <Th>Município/UF</Th>
              <Th>Áreas</Th>
              <Th align="right">Score</Th>
              <Th align="right">Pend.</Th>
              <Th align="right">Venc.</Th>
              <Th align="right">Sem docs</Th>
              <Th>Principal desconformidade</Th>
              <Th>Ação</Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((cliente) => (
              <ConfClienteTableRow key={cliente.cpf} cliente={cliente} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <td
      className={`px-3 py-2 border-t border-border align-top ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {children}
    </td>
  );
}

function ConfClienteTableRow({ cliente }: { cliente: ConfCliente }) {
  const [open, setOpen] = useState(false);
  const areas = cliente.areas || [];
  const vencidos = areas.reduce(
    (s, a) => s + a.conformidade.checks.filter((c) => c.status === 'vencido').length,
    0,
  );
  const semDocs = areas.filter((a) => a.conformidade.docs_relacionados === 0).length;
  const principal = areas.flatMap((a) =>
    a.conformidade.checks.filter((c) => c.status !== 'ok').map((check) => ({ area: a, check })),
  )[0];
  const principalTexto = principal
    ? `${areaTitle(principal.area)}: ${principal.check.label}`
    : 'Sem desconformidade crítica';
  const scoreTone = cliente.score >= 80 ? 'ok' : cliente.score >= 55 ? 'warn' : 'danger';

  return (
    <>
      <tr className="hover:bg-surface2/60">
        <Td>
          <button type="button" onClick={() => setOpen((v) => !v)} className="text-left">
            <div className="font-semibold text-text whitespace-nowrap hover:underline">
              {cliente.nome}
            </div>
            <div className="text-xs text-text-muted whitespace-nowrap">{cliente.cpf}</div>
          </button>
        </Td>
        <Td>
          <span className="whitespace-nowrap">
            {cliente.municipio || '—'}
            {cliente.uf ? `/${cliente.uf}` : ''}
          </span>
        </Td>
        <Td>
          <div className="flex flex-wrap gap-1.5">
            <Chip tone="neutral" size="sm">
              {areas.length} área(s)
            </Chip>
            <Chip tone="info" size="sm">
              {areas.filter((a) => a.kind === 'arrendamento').length} arrend.
            </Chip>
          </div>
        </Td>
        <Td align="right">
          <Chip tone={scoreTone} size="sm">
            {cliente.score}%
          </Chip>
        </Td>
        <Td align="right">
          <span
            className={cliente.pendencias ? 'text-warning font-semibold' : 'text-ok font-semibold'}
          >
            {cliente.pendencias}
          </span>
        </Td>
        <Td align="right">
          <span className={vencidos ? 'text-danger font-semibold' : 'text-text-muted'}>
            {vencidos}
          </span>
        </Td>
        <Td align="right">
          <span className={semDocs ? 'text-danger font-semibold' : 'text-text-muted'}>
            {semDocs}
          </span>
        </Td>
        <Td>
          <span className={cliente.pendencias ? 'text-danger' : 'text-ok'}>{principalTexto}</span>
        </Td>
        <Td>
          <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? 'Ocultar' : 'Abrir relatório'}
          </Button>
        </Td>
      </tr>
      {open && (
        <tr>
          <td colSpan={9} className="px-3 py-4 border-t border-border bg-surface2/40">
            <ClienteAuditoriaResumo cliente={cliente} />
          </td>
        </tr>
      )}
    </>
  );
}

function ClienteAuditoriaResumo({ cliente }: { cliente: ConfCliente }) {
  const [openAreas, setOpenAreas] = useState<Record<string, boolean>>({});
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="text-base font-semibold text-text">
            {cliente.nome} &gt; Resumo para auditoria dos documentos
          </div>
          <div className="text-xs text-text-muted">
            {cliente.cpf} · {cliente.municipio || '—'}
            {cliente.uf ? `/${cliente.uf}` : ''}
          </div>
          {cliente.instituicoes.length > 0 && (
            <div className="text-xs text-text-muted mt-1">
              Bancos: {cliente.instituicoes.join(', ')}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => abrirRelatorioConformidade(cliente, 'compacto')}
          >
            PDF compacto
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => abrirRelatorioConformidade(cliente, 'expandido')}
          >
            PDF expandido
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void baixarRelatorioOnus(cliente)}>
            PDF ônus/gravames
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <ConfStat label="Áreas" value={cliente.areas.length} />
        <ConfStat label="Pendências" value={cliente.pendencias} danger />
        <ConfStat
          label="Sem documentos"
          value={cliente.areas.filter((a) => a.conformidade.docs_relacionados === 0).length}
          danger
        />
        <ConfStat label="Score" value={cliente.score} />
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-surface">
        <table className="w-full text-xs">
          <thead className="bg-elevated text-text-muted">
            <tr>
              <Th>Tipo</Th>
              <Th>Área / Matrícula</Th>
              <Th align="right">Score</Th>
              <Th align="right">Pend.</Th>
              <Th>Ação</Th>
            </tr>
          </thead>
          <tbody>
            {cliente.areas.map((area) => {
              const areaKey = `${area.kind}-${area.id}`;
              const pendentes = area.conformidade.checks.filter((c) => c.status !== 'ok');
              const documentos = area.conformidade.documentos ?? [];
              const tone =
                area.conformidade.score >= 80
                  ? 'ok'
                  : area.conformidade.score >= 55
                    ? 'warn'
                    : 'danger';
              const open = !!openAreas[areaKey];
              return (
                <Fragment key={areaKey}>
                  <tr>
                    <Td>
                      <Chip tone={area.kind === 'arrendamento' ? 'info' : 'neutral'} size="sm">
                        {area.kind === 'arrendamento' ? 'Arrendamento' : 'Imóvel próprio'}
                      </Chip>
                    </Td>
                    <Td>
                      <div className="font-medium text-text min-w-[220px]">{areaTitle(area)}</div>
                      <div className="text-[0.68rem] text-text-muted">
                        {area.area_ha
                          ? `${Number(area.area_ha).toLocaleString('pt-BR')} ha`
                          : 'área não informada'}
                      </div>
                    </Td>
                    <Td align="right">
                      <Chip tone={tone} size="sm">
                        {area.conformidade.score}%
                      </Chip>
                    </Td>
                    <Td align="right">
                      <span
                        className={
                          pendentes.length ? 'text-warning font-semibold' : 'text-ok font-semibold'
                        }
                      >
                        {pendentes.length}
                      </span>
                    </Td>
                    <Td>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="self-start text-[0.68rem]"
                        onClick={() => setOpenAreas((v) => ({ ...v, [areaKey]: !v[areaKey] }))}
                      >
                        {open ? 'Ocultar pendência do imóvel' : 'Ver pendência do imóvel'}
                      </Button>
                    </Td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={5} className="bg-elevated/40 p-3 border-t border-border">
                        <div className="rounded-lg border border-border bg-surface p-3">
                          <div className="text-sm font-semibold text-text">
                            {areaTitle(area)} &gt; Pendências detalhadas
                          </div>
                          <div className="text-xs text-text-muted mb-3">
                            As pendências ficam vinculadas à matrícula/imóvel correspondente.
                          </div>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            <div>
                              <div className="text-[0.68rem] font-semibold uppercase text-text-muted mb-2">
                                Apontamentos
                              </div>
                              {pendentes.length > 0 ? (
                                <ul className="space-y-2">
                                  {pendentes.map((check) => (
                                    <ConfCheckRow key={check.label} check={check} />
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-xs text-ok">
                                  Sem pendência crítica nesta matrícula.
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="text-[0.68rem] font-semibold uppercase text-text-muted mb-2">
                                Documentos relacionados
                              </div>
                              {documentos.length > 0 ? (
                                <ul className="space-y-2">
                                  {documentos.map((doc, idx) => (
                                    <ConfDocumentoRow key={`${doc.id ?? idx}`} doc={doc} />
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-xs text-danger">
                                  Nenhum documento relacionado a esta matrícula/imóvel.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function areaTitle(area: ConfArea) {
  if (area.kind === 'arrendamento') {
    // Nunca o ID interno como nome: quem lê a tela não reconhece "Arrendamento 25".
    const base = area.matricula
      ? `${area.nome || 'Arrendamento'} · Mat. ${area.matricula}`
      : `${area.nome || 'Arrendamento — imóvel não identificado'}`;
    return area.arrendador_nome ? `${base} · Arrendante: ${area.arrendador_nome}` : base;
  }
  return `${area.nome || 'Imóvel'}${area.matricula ? ` · Mat. ${area.matricula}` : ''}`;
}

function ConfCheckRow({ check }: { check: ConfCheck }) {
  const Icon =
    check.status === 'ok' ? CheckCircle2 : check.status === 'vencido' ? XCircle : AlertTriangle;
  const cls =
    check.status === 'ok' ? 'text-ok' : check.status === 'vencido' ? 'text-danger' : 'text-warning';
  return (
    <li className="flex gap-2 text-xs rounded-md border border-border bg-elevated p-2">
      <Icon size={14} className={`shrink-0 mt-0.5 ${cls}`} />
      <div>
        <div className="font-semibold text-text">{check.label}</div>
        <div className="text-text-muted">{check.detalhe || '—'}</div>
      </div>
    </li>
  );
}

function ConfDocumentoRow({ doc }: { doc: z.infer<typeof confDocumentoSchema> }) {
  const validade = doc.validade ? new Date(doc.validade).toLocaleDateString('pt-BR') : null;
  return (
    <li className="text-xs rounded-md border border-border bg-elevated p-2">
      <div className="font-semibold text-text">
        {doc.descricao || doc.tipo || `Documento #${doc.id ?? '—'}`}
      </div>
      <div className="text-text-muted">
        {[
          doc.tipo,
          doc.status ? `status: ${doc.status}` : null,
          validade ? `validade: ${validade}` : null,
        ]
          .filter(Boolean)
          .join(' · ') || 'Documento vinculado.'}
      </div>
    </li>
  );
}

// ─── Pipe Grupo Econômico ────────────────────────────────────────────────
interface GrupoEconomicoPipeProps {
  grupoId: number | null;
  onBackToPipes: () => void;
  onSelectGrupo: (id: number) => void;
  onBackToGrupos: () => void;
  onSelectCliente: (cpf: string) => void;
}

function GrupoEconomicoPipe({
  grupoId,
  onBackToPipes,
  onSelectGrupo,
  onBackToGrupos,
  onSelectCliente,
}: GrupoEconomicoPipeProps) {
  if (grupoId) {
    return (
      <GrupoClientesView
        grupoId={grupoId}
        onBack={onBackToGrupos}
        onSelectCliente={onSelectCliente}
      />
    );
  }
  return <GruposListView onBack={onBackToPipes} onSelect={onSelectGrupo} />;
}

function GruposListView({
  onBack,
  onSelect,
}: {
  onBack: () => void;
  onSelect: (id: number) => void;
}) {
  const { data: grupos = [], isLoading, error } = useGruposEconomicos();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <PageShell
      eyebrow="Negócios · Cliente"
      title="Grupo Econômico"
      lead="Carteira agrupada por holding ou estrutura societária."
      back={
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft size={14} strokeWidth={1.5} />
          Voltar
        </Button>
      }
    >
      <Stack gap="lg">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 md:max-w-md">
            <Search
              size={14}
              strokeWidth={1.5}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou CNPJ"
              className="rounded-full pr-9"
            />
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus size={14} strokeWidth={1.5} />
            Novo grupo
          </Button>
          <Chip tone="neutral" size="sm" className="ml-auto">
            {grupos.length} grupos
          </Chip>
        </div>

        {isLoading ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">Carregando grupos…</p>
          </Card>
        ) : error ? (
          <Card padding="lg">
            <p className="text-sm text-danger">
              {error instanceof ApiError ? error.message : 'Falha ao carregar grupos.'}
            </p>
          </Card>
        ) : grupos.length === 0 ? (
          <Card padding="lg">
            <Stack gap="sm">
              <p className="text-sm text-text-muted">Nenhum grupo econômico cadastrado ainda.</p>
              <div>
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus size={14} strokeWidth={1.5} />
                  Criar primeiro grupo
                </Button>
              </div>
            </Stack>
          </Card>
        ) : (
          <GrupoList grupos={grupos} search={search} onSelect={onSelect} />
        )}

        <GrupoEconomicoModal open={createOpen} onOpenChange={setCreateOpen} />
      </Stack>
    </PageShell>
  );
}

function GrupoClientesView({
  grupoId,
  onBack,
  onSelectCliente,
}: {
  grupoId: number;
  onBack: () => void;
  onSelectCliente: (cpf: string) => void;
}) {
  const { data, isLoading, error } = useClientesDoGrupo(grupoId);
  const { data: imoveisData } = useImoveisDoGrupo(grupoId);
  const imoveisDiretos = imoveisData?.imoveis ?? [];
  const resumoImoveis = imoveisData?.resumo;
  const { data: grupos = [] } = useGruposEconomicos();
  const [imovelDetail, setImovelDetail] = useState<ImovelResumo | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusCliente | ''>('');
  const [ufFilter, setUfFilter] = useState('');
  const [municipioFilter, setMunicipioFilter] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  const grupo = data?.grupo;
  const clientes = data?.clientes ?? [];
  const grupoFull = grupos.find((g) => g.id === grupoId) ?? null;

  const ufsDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const c of clientes) if (c.uf) set.add(c.uf.toUpperCase());
    return Array.from(set).sort();
  }, [clientes]);

  const municipiosDisponiveis = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const c of clientes) {
      if (!c.municipio) continue;
      if (ufFilter && (c.uf ?? '').toUpperCase() !== ufFilter.toUpperCase()) continue;
      const key = c.municipio.toLowerCase().trim();
      if (!byKey.has(key)) byKey.set(key, formatName(c.municipio));
    }
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [clientes, ufFilter]);

  return (
    <PageShell
      eyebrow="Grupo Econômico"
      title={grupo?.nome ?? 'Carregando…'}
      lead="Clientes vinculados a este grupo econômico."
      back={
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft size={14} strokeWidth={1.5} />
          Voltar
        </Button>
      }
    >
      <Stack gap="lg">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 md:max-w-md">
            <Search
              size={14}
              strokeWidth={1.5}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, CPF ou município"
              className="rounded-full pr-9"
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusCliente | '')}
            className="text-[0.82rem]"
          >
            <option value="">Todos status</option>
            {STATUS_CLIENTE.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
          {ufsDisponiveis.length > 1 && (
            <Select
              value={ufFilter}
              onChange={(e) => {
                setUfFilter(e.target.value);
                setMunicipioFilter('');
              }}
              className="text-[0.82rem]"
            >
              <option value="">Todas UFs</option>
              {ufsDisponiveis.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </Select>
          )}
          {municipiosDisponiveis.length > 1 && (
            <Select
              value={municipioFilter}
              onChange={(e) => setMunicipioFilter(e.target.value)}
              className="text-[0.82rem]"
            >
              <option value="">Todos municípios</option>
              {municipiosDisponiveis.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          )}
          <Button variant="ghost" onClick={() => setEditOpen(true)} disabled={!grupoFull}>
            Editar grupo
          </Button>
          <Chip tone="neutral" size="sm" className="ml-auto">
            {clientes.length} clientes
          </Chip>
        </div>

        {imoveisDiretos.length > 0 && (
          <Stack gap="sm">
            {resumoImoveis && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border-subtle bg-surface-subtle px-3 py-2 text-xs text-text-2">
                <span>
                  <strong className="font-semibold text-text">Área física sem duplicidade:</strong>{' '}
                  {resumoImoveis.area_total_sem_duplicidade_ha.toLocaleString('pt-BR', {
                    maximumFractionDigits: 2,
                  })}{' '}
                  ha
                </span>
                <span>{resumoImoveis.imoveis_unicos} imóveis únicos</span>
                {resumoImoveis.registros_apoio_nao_somados > 0 && (
                  <span>
                    {resumoImoveis.registros_apoio_nao_somados} registros de CAR agrupado ocultos
                    para evitar repetição
                  </span>
                )}
                <span>{resumoImoveis.vinculos_ativos} vínculos ativos — não somam nova área</span>
              </div>
            )}
            <table className="ds-table">
              <thead>
                <tr>
                  <th>Denominação</th>
                  <th>Matrícula</th>
                  <th>Município</th>
                  <th>CAR</th>
                  <th>Hectares</th>
                </tr>
              </thead>
              <tbody>
                {imoveisDiretos.map((i) => (
                  <tr key={i.id} onClick={() => setImovelDetail(i)} className="cursor-pointer">
                    <td className="text-sm text-text">{i.denominacao ?? '—'}</td>
                    <td className="text-sm text-text-2 tabular-nums">{i.matricula ?? '—'}</td>
                    <td className="text-sm text-text-2">
                      {i.municipio ?? '—'}
                      {i.uf && ` / ${i.uf}`}
                    </td>
                    <td className="text-sm text-text-2 tabular-nums">{i.car_codigo ?? '—'}</td>
                    <td>
                      {i.area_total_ha != null ? (
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Chip tone={i.contabiliza_area ? 'info' : 'neutral'} size="sm">
                            {i.area_total_ha.toLocaleString('pt-BR', {
                              maximumFractionDigits: 2,
                            })}{' '}
                            ha
                            {!i.contabiliza_area && ' · não somada'}
                          </Chip>
                          <span
                            className="text-xs font-medium text-text-2"
                            title="Titularidade cadastral do imóvel"
                          >
                            ·{' '}
                            {i.titularidade.tipo === 'holding'
                              ? 'Titular'
                              : i.titularidade.tipo === 'copropriedade'
                                ? 'Copropriedade'
                                : 'Próprio'}
                            : {formatName(i.titularidade.nome)}
                          </span>
                          {i.vinculos.map((vinculo) => (
                            <span
                              key={vinculo.id}
                              className="text-xs font-medium text-text-2"
                              title="Vínculo de uso; a área física não é somada novamente"
                            >
                              · {vinculo.tipo}: {formatName(vinculo.beneficiario)}
                            </span>
                          ))}
                          {!i.contabiliza_area && i.motivo_nao_contabiliza && (
                            <span className="text-[11px] text-text-muted">
                              {i.motivo_nao_contabiliza}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Stack>
        )}

        {isLoading ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">Carregando clientes…</p>
          </Card>
        ) : error ? (
          <Card padding="lg">
            <p className="text-sm text-danger">
              {error instanceof ApiError ? error.message : 'Falha ao carregar clientes.'}
            </p>
          </Card>
        ) : clientes.length === 0 ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">
              {imoveisDiretos.length > 0
                ? 'Sem clientes PF — esse grupo é PJ pura (CNPJ é o proprietário direto dos imóveis acima).'
                : 'Nenhum cliente vinculado a este grupo. Vincule um cliente editando sua ficha.'}
            </p>
          </Card>
        ) : (
          <ClienteList
            clientes={clientes}
            search={search}
            statusFilter={statusFilter}
            ufFilter={ufFilter}
            municipioFilter={municipioFilter}
            onSelect={onSelectCliente}
          />
        )}

        <GrupoEconomicoModal
          open={editOpen}
          onOpenChange={setEditOpen}
          grupo={grupoFull}
          onDeleted={onBack}
        />

        <ImovelDetailModal
          imovel={imovelDetail}
          cpfDono={null}
          nomeDono={grupo?.nome ?? null}
          onClose={() => setImovelDetail(null)}
        />
      </Stack>
    </PageShell>
  );
}
